import React, { useState, useEffect, useMemo } from "react";
import { Button, Typography, Paper, TextField } from "@mhome/ui";
import { CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  listScopeInfo,
  listScopePreferences,
  saveScope,
  updateMember,
  listInvitation,
  saveLocation,
} from "../service/scope-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import { useSetRecoilState } from "recoil";
import { scopesState, scopePreferenceState } from "../global-state";
import LocationDialog from "./location-dialog";
import { useAppInterface } from "../app/app-interface-provider";

function InitScope() {
  const history = useHistory();
  const isMobile = useMediaQuery("@media (max-width:599px)");
  const [loading, setLoading] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [loadingInvitationId, setLoadingInvitationId] = useState(null);
  const [newScopeName, setNewScopeName] = useState("");
  const [selectedHomeLocation, setSelectedHomeLocation] = useState(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [showJoinScope, setShowJoinScope] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const setScopes = useSetRecoilState(scopesState);
  const setScopePreference = useSetRecoilState(scopePreferenceState);
  const { successToast, errorToast } = useToast();
  const { sendAndWaitWs, changeActiveScope } = useWebSocket();
  const [isAccept, setIsAccept] = useState(false);
  const [spaceCreated, setSpaceCreated] = useState(false);
  const appInterface = useAppInterface();

  useEffect(() => {
    if (showJoinScope) {
      fetchInvitations();
    }
  }, [showJoinScope]);

  const fetchInvitations = async () => {
    setInvitationLoading(true);
    setLoading(true);
    const resp = await listInvitation(sendAndWaitWs);
    if (resp && !resp.error) {
      if (Array.isArray(resp) && resp.length > 0) {
        setInvitations(resp);
      } else {
        setInvitations([]);
      }
    } else {
      errorToast("Failed to load invitations");
    }
    setInvitationLoading(false);
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!newScopeName.trim() || !selectedHomeLocation) return;

    setLoading(true);
    const resp = await saveScope(sendAndWaitWs, "new", newScopeName, true);
    if (resp && !resp.error) {
      const scopeId = resp.scopeId;
      if (scopeId && typeof scopeId === "string") {
        changeActiveScope(scopeId);
        const locationData = {
          name: "Home",
          address: selectedHomeLocation.address,
          latitude: selectedHomeLocation.latitude,
          longitude: selectedHomeLocation.longitude,
          timezone: selectedHomeLocation.timezone,
          locationId: "home",
        };
        await saveLocation(sendAndWaitWs, scopeId, locationData);
        const scopeResp = await listScopeInfo(sendAndWaitWs);
        const scopePreferencesResp = await listScopePreferences(sendAndWaitWs);
        setScopes(scopeResp);
        setScopePreference(scopePreferencesResp);
        if (appInterface.isAvailable()) {
          await appInterface.saveScopes({
            scopes: scopeResp,
            activeScope: scopeId,
            scopePreferences: scopePreferencesResp,
          });
        }
        successToast("Space and home location created successfully!");
        setSpaceCreated(true);
      } else {
        errorToast("Failed to create space!");
      }
    } else {
      errorToast("Failed to create space");
    }
    setLoading(false);
  };

  const handleRejectInvitation = async (invitationScopeId) => {
    setLoadingInvitationId(invitationScopeId);
    try {
      let scopeId = null;
      invitations
        .filter((inv) => inv.scopeId === invitationScopeId)
        .map((inv) => {
          scopeId = inv.scopeId;
        });

      if (!scopeId) {
        errorToast("The space does not exist.Please refresh");
        return;
      }
      const resp = await updateMember(sendAndWaitWs, scopeId, false);
      if (resp && !resp.error) {
        successToast("Successfully reject the invitation");
        setInvitations(
          invitations.filter((inv) => inv.scopeId !== invitationScopeId)
        );
        await fetchInvitations();
      } else {
        errorToast("Failed to reject invitation!");
      }
    } catch (error) {
      console.error("Error rejecting invitation:", error);
      errorToast("Failed to reject invitation");
    } finally {
      setLoadingInvitationId(null);
    }
  };

  const handleAcceptInvitation = async (invitationScopeId) => {
    setLoadingInvitationId(invitationScopeId);
    let scopeId = null;
    invitations
      .filter((inv) => inv.scopeId === invitationScopeId)
      .map((inv) => {
        scopeId = inv.scopeId;
      });
    if (!scopeId) {
      errorToast("The space does not exist. Please refresh");
      return;
    }
    const resp = await updateMember(sendAndWaitWs, scopeId, true, true);
    if (resp && !resp.error) {
      changeActiveScope(scopeId);
      successToast("Successfully joined the space");
      setInvitations(
        invitations.filter((inv) => inv.scopeId !== invitationScopeId)
      );
      setIsAccept(true);
      await fetchInvitations();
    } else {
      errorToast("Failed to accept invitation!");
    }
    setLoadingInvitationId(null);
  };

  const toggleView = () => {
    setShowJoinScope(!showJoinScope);
  };

  const goToIntegration = () => {
    history.replace("/integrations");
  };

  const goToDashboard = () => {
    history.replace("/dashboard");
  };

  const handleLocationSave = (locationData) => {
    setSelectedHomeLocation(locationData);
    setLocationDialogOpen(false);
  };

  const isFormValid = newScopeName.trim() && selectedHomeLocation;

  // Helper function to convert MUI spacing to pixels
  const spacingToPx = (value) => {
    if (typeof value === "number") {
      return `${value * 8}px`;
    }
    return value;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "hsl(var(--background))",
        padding: isMobile ? 0 : spacingToPx(2),
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 3}
        style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 480,
          minHeight: isMobile ? "100vh" : "auto",
          borderRadius: isMobile ? 0 : spacingToPx(4),
          overflow: "hidden",
          backgroundColor: "hsl(var(--card))",
          display: "flex",
          flexDirection: "column",
          justifyContent: isMobile ? "flex-start" : "center",
        }}
      >
        <div
          style={{
            textAlign: "center",
            paddingTop: isMobile ? spacingToPx(10) : spacingToPx(4),
            paddingBottom: spacingToPx(3),
            paddingLeft: spacingToPx(3),
            paddingRight: spacingToPx(3),
            flex: 1,
            display: "flex",
            flexDirection: "column",
            maxWidth: 480,
            marginLeft: "auto",
            marginRight: "auto",
            width: "100%",
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            style={{
              fontWeight: 600,
              marginBottom: spacingToPx(1),
            }}
          >
            {showJoinScope ? "Join Space" : "Create Space"}
          </Typography>

          <Typography
            variant="body1"
            style={{
              color: "hsl(var(--muted-foreground))",
              marginBottom: spacingToPx(3),
            }}
          >
            {spaceCreated
              ? "Your space has been created! What would you like to do next?"
              : showJoinScope
              ? "View and accept your space invitations"
              : "Let's create your space and set up your home location"}
          </Typography>

          {spaceCreated ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacingToPx(2),
                width: "100%",
                paddingLeft: isMobile ? spacingToPx(2) : 0,
                paddingRight: isMobile ? spacingToPx(2) : 0,
              }}
            >
              <Button
                variant="contained"
                onClick={goToDashboard}
                style={{
                  paddingTop: spacingToPx(1.5),
                  paddingBottom: spacingToPx(1.5),
                  textTransform: "none",
                  fontSize: "1.1rem",
                  backgroundColor: "hsl(var(--primary))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "hsl(var(--primary-foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
                }}
              >
                Home
              </Button>

              <Button
                variant="outlined"
                onClick={goToIntegration}
                style={{
                  paddingTop: spacingToPx(1.5),
                  paddingBottom: spacingToPx(1.5),
                  textTransform: "none",
                  fontSize: "1.1rem",
                  borderColor: "hsl(var(--primary))",
                  color: "hsl(var(--primary))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    "hsl(var(--primary-foreground))";
                  e.currentTarget.style.color =
                    "hsl(var(--primary-foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "hsl(var(--primary))";
                  e.currentTarget.style.color = "hsl(var(--primary))";
                }}
              >
                Setup Integrations
              </Button>
            </div>
          ) : !showJoinScope ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacingToPx(3),
                width: "100%",
                paddingLeft: isMobile ? spacingToPx(2) : 0,
                paddingRight: isMobile ? spacingToPx(2) : 0,
              }}
            >
              <TextField
                autoFocus
                fullWidth
                required
                name="name"
                value={newScopeName}
                onChange={(e) => {
                  setNewScopeName(e.target.value);
                }}
                variant="outlined"
                placeholder="Space Name"
                disabled={loading}
              />

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setLocationDialogOpen(true)}
                startIcon={<LocationOnIcon />}
                disabled={loading}
                style={{
                  paddingTop: spacingToPx(1.5),
                  paddingBottom: spacingToPx(1.5),
                  textTransform: "none",
                  justifyContent: "flex-start",
                  color: selectedHomeLocation
                    ? "hsl(var(--foreground))"
                    : "hsl(var(--muted-foreground))",
                  borderColor: selectedHomeLocation
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "hsl(var(--primary))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = selectedHomeLocation
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))";
                }}
              >
                {selectedHomeLocation
                  ? selectedHomeLocation.address
                  : "Select Home Location"}
              </Button>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isFormValid}
                style={{
                  paddingTop: spacingToPx(1.5),
                  paddingBottom: spacingToPx(1.5),
                  textTransform: "none",
                  fontSize: "1.1rem",
                  backgroundColor: "hsl(var(--primary))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "hsl(var(--primary-foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Create My Space"
                )}
              </Button>

              <Button
                variant="text"
                onClick={toggleView}
                style={{
                  textTransform: "none",
                  color: "hsl(var(--muted-foreground))",
                  fontSize: "0.875rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                Skip creation and join space
              </Button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacingToPx(3),
                width: "100%",
                paddingLeft: isMobile ? spacingToPx(2) : 0,
                paddingRight: isMobile ? spacingToPx(2) : 0,
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchInvitations}
                  disabled={invitationLoading}
                  style={{ textTransform: "none" }}
                ></Button>
              </div>

              {invitationLoading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: spacingToPx(4),
                    paddingBottom: spacingToPx(4),
                  }}
                >
                  <CircularProgress size={24} />
                </div>
              )}

              {!invitationLoading &&
                invitations.length > 0 &&
                invitations.map((invitation, index) => (
                  <Paper
                    key={`${invitation.scopeId}-${index}`}
                    style={{
                      padding: spacingToPx(1.5),
                      border: `1px solid hsl(var(--border))`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        <Typography style={{ marginRight: spacingToPx(1) }}>
                          {invitation.inviterName} invite you to join&nbsp;
                          {invitation.scopeName}
                        </Typography>
                      </div>
                      <div style={{ marginTop: spacingToPx(1) }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleAcceptInvitation(invitation.scopeId)
                          }
                          disabled={loadingInvitationId === invitation.scopeId}
                        >
                          {loadingInvitationId === invitation.scopeId ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Accept ✔"
                          )}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          style={{ marginLeft: spacingToPx(1) }}
                          onClick={() =>
                            handleRejectInvitation(invitation.scopeId)
                          }
                          disabled={loadingInvitationId === invitation.scopeId}
                        >
                          {loadingInvitationId === invitation.scopeId ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Reject ✘"
                          )}
                        </Button>
                      </div>
                    </div>
                  </Paper>
                ))}

              {!invitationLoading && invitations.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: spacingToPx(2),
                    color: "hsl(var(--muted-foreground))",
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{ marginBottom: spacingToPx(2) }}
                  >
                    No invitations found
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: "hsl(var(--muted-foreground))" }}
                  >
                    You haven&apos;t received any space invitations yet
                  </Typography>
                </div>
              )}

              <Button
                variant="contained"
                onClick={goToIntegration}
                disabled={loading || !isAccept}
                style={{
                  marginTop: spacingToPx(2),
                  textTransform: "none",
                  fontSize: "1.1rem",
                  backgroundColor: "hsl(var(--primary))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "hsl(var(--primary-foreground))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Enter My Scope"
                )}
              </Button>

              <Button
                variant="text"
                onClick={toggleView}
                startIcon={<ArrowBackIcon />}
                style={{
                  textTransform: "none",
                  color: "hsl(var(--muted-foreground))",
                  fontSize: "0.875rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                Back to create space
              </Button>
            </div>
          )}
        </div>
      </Paper>

      <LocationDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        onSave={handleLocationSave}
        isHomeLocation={true}
      />
    </div>
  );
}

export default InitScope;
